/**
 * StatsPage — 统计仪表盘首页（docs/ui_topbar §3.2 PageTitle + ui_page §4.5 字段帮助）
 * 顶部 4 个统计卡片 (2x2) + 队列统计 + 底部存储信息
 * 使用 React Query 获取统计数据
 */
import type { ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Card,
  Text,
  Spinner,
  Badge,
  Button,
  Divider,
} from '@fluentui/react-components'
import {
  GlobeRegular,
  PeopleRegular,
  MailRegular,
  TextBulletListSquareRegular,
  DatabaseRegular,
  CloudRegular,
  MailInboxRegular,
  SendRegular,
  LockClosedRegular,
  ArrowSyncRegular,
} from '@fluentui/react-icons'
import { statsApi } from '@/api/stats'
import apiClient, { unwrap } from '@/api/client'
import type { HealthInfo, QueueStats } from '@/api/types'
import { tlsApi } from '@/api/tls'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'
import { PageTitle } from '@/components/PageBreadcrumb'
import { InfoTip } from '@/components/InfoTip'

// ── 辅助: 字节转 MB 字符串 ──
function bytesToMB(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB'
  const mb = bytes / (1024 * 1024)
  if (mb < 1) return `${mb.toFixed(2)} MB`
  if (mb < 100) return `${mb.toFixed(1)} MB`
  return `${mb.toFixed(0)} MB`
}

// ── 统计卡片配置 ──
type StatKey = 'domains' | 'users' | 'messages' | 'queue_pending'
type Accent = 'brand' | 'success' | 'warning' | 'danger'

interface StatCardConfig {
  key: StatKey
  label: string
  help: ReactNode
  renderIcon: (cls: string) => ReactNode
  accent: Accent
  badge?: (value: number) => ReactNode
}

const STAT_CARDS: StatCardConfig[] = [
  {
    key: 'domains',
    label: '域名数',
    help: <>收件域与投递域的总数。新增/编辑后立即生效（DB 驱动）。</>,
    renderIcon: (cls) => <GlobeRegular className={cls} />,
    accent: 'brand',
  },
  {
    key: 'users',
    label: '用户数',
    help: <>邮箱账号总数，含启用与停用状态。</>,
    renderIcon: (cls) => <PeopleRegular className={cls} />,
    accent: 'success',
  },
  {
    key: 'messages',
    label: '邮件数',
    help: <>已存储的邮件总数（含收件与投递记录）。</>,
    renderIcon: (cls) => <MailRegular className={cls} />,
    accent: 'warning',
  },
  {
    key: 'queue_pending',
    label: '待发队列',
    help: <>等待投递的邮件数。{'>'}0 时表示有邮件尚未送达。</>,
    renderIcon: (cls) => <TextBulletListSquareRegular className={cls} />,
    accent: 'danger',
    badge: (v) =>
      v > 0 ? (
        <Badge appearance="tint" color="danger">
          待处理
        </Badge>
      ) : null,
  },
]

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  centered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 0',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
  },
  statCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '20px',
  },
  statTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  iconBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: tokens.borderRadiusMedium,
    flexShrink: 0,
  },
  iconBoxBrand: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  iconBoxSuccess: {
    backgroundColor: tokens.colorStatusSuccessBackground2,
    color: tokens.colorStatusSuccessForeground1,
  },
  iconBoxWarning: {
    backgroundColor: tokens.colorStatusWarningBackground2,
    color: tokens.colorStatusWarningForeground1,
  },
  iconBoxDanger: {
    backgroundColor: tokens.colorStatusDangerBackground2,
    color: tokens.colorStatusDangerForeground1,
  },
  statIcon: {
    fontSize: '22px',
  },
  statValue: {
    fontSize: '30px',
    fontWeight: 700,
    color: tokens.colorNeutralForeground1,
    lineHeight: '1',
  },
  statLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statLabel: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  queueGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '16px',
  },
  queueCard: {
    padding: '16px',
  },
  queueLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: tokens.colorNeutralForeground2,
  },
  queueValue: {
    fontSize: '24px',
    fontWeight: 700,
    marginTop: '8px',
  },
  storageGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
  },
  storageCard: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '14px',
    padding: '20px',
  },
  storageContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  storageValue: {
    fontSize: '20px',
    fontWeight: 700,
    color: tokens.colorNeutralForeground1,
  },
  storageLabel: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
  statusGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '10px 24px',
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
})

export default function StatsPage() {
  const styles = useStyles()
  const queryClient = useQueryClient()
  const { data: stats, isLoading } = useQuery({
    queryKey: ['stats'],
    queryFn: () => statsApi.get(),
  })

  const { data: health } = useQuery({
    queryKey: ['health'],
    queryFn: () => unwrap<HealthInfo>(apiClient.get('/health')),
  })
  const { data: queueStats } = useQuery({
    queryKey: ['queue-stats'],
    queryFn: () => unwrap<QueueStats>(apiClient.get('/queue/stats')),
  })
  const { data: tlsConfig } = useQuery({
    queryKey: ['tls-config'],
    queryFn: () => tlsApi.getConfig(),
  })
  const reloadMutation = useMutation({
    mutationFn: () => tlsApi.reload(),
    onSuccess: async (res) => {
      await queryClient.invalidateQueries({ queryKey: ['tls-config'] })
      toast.success('已重载', res.message || 'TLS 已热重载')
    },
    onError: (err) => toast.error('重载失败', err instanceof ApiError ? err.message : '重载失败'),
  })

  if (isLoading) {
    return (
      <div className={styles.centered}>
        <Spinner size="large" />
      </div>
    )
  }

  if (!stats) {
    return (
      <div className={styles.centered}>
        <Text>暂无统计数据</Text>
      </div>
    )
  }

  const accentClass: Record<Accent, string> = {
    brand: styles.iconBoxBrand,
    success: styles.iconBoxSuccess,
    warning: styles.iconBoxWarning,
    danger: styles.iconBoxDanger,
  }

  return (
    <div className={styles.root}>
      {/* 页面标题：面包屑负责"我在哪"，PageTitle 负责"这里是什么"（docs/ui_topbar §3.2） */}
      <PageTitle subtitle="系统运行概览">状态</PageTitle>

      {/* 服务器健康状态 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        {health ? (
          health.status === 'ok' || health.status === 'healthy' ? (
            <Badge appearance="filled" color="success">运行正常</Badge>
          ) : (
            <Badge appearance="filled" color="danger">状态异常</Badge>
          )
        ) : (
          <Badge appearance="filled" color="subtle">无法连接</Badge>
        )}
        {health?.version && <Badge appearance="outline" color="brand">v{health.version}</Badge>}
      </div>

      {/* 队列统计 */}
      {queueStats && (
        <div className={styles.queueGrid}>
          <Card className={styles.queueCard}>
            <div className={styles.queueLabel}><MailInboxRegular /> 收件队列</div>
            <div className={styles.queueValue}>{queueStats.incoming}</div>
          </Card>
          <Card className={styles.queueCard}>
            <div className={styles.queueLabel}><SendRegular /> 处理中</div>
            <div className={styles.queueValue}>{queueStats.active}</div>
          </Card>
          <Card className={styles.queueCard}>
            <div className={styles.queueLabel}>延迟</div>
            <div className={styles.queueValue}>{queueStats.deferred}</div>
          </Card>
          <Card className={styles.queueCard}>
            <div className={styles.queueLabel}>退信</div>
            <div className={styles.queueValue}>{queueStats.bounced}</div>
          </Card>
          <Card className={styles.queueCard}>
            <div className={styles.queueLabel}>已投递</div>
            <div className={styles.queueValue}>{queueStats.delivered}</div>
          </Card>
        </div>
      )}

      <div className={styles.statsGrid}>
        {STAT_CARDS.map((c) => (
          <Card key={c.key} className={styles.statCard}>
            <div className={styles.statTop}>
              <div className={`${styles.iconBox} ${accentClass[c.accent]}`}>
                {c.renderIcon(styles.statIcon)}
              </div>
              <Text className={styles.statValue}>
                {stats[c.key].toLocaleString()}
              </Text>
            </div>
            <div className={styles.statLabelRow}>
              <Text className={styles.statLabel}>{c.label}</Text>
              <InfoTip title={c.label} content={c.help} />
              {c.badge?.(stats[c.key])}
            </div>
          </Card>
        ))}
      </div>

      <Text className={styles.sectionTitle}>存储信息</Text>
      <div className={styles.storageGrid}>
        <Card className={styles.storageCard}>
          <div className={`${styles.iconBox} ${styles.iconBoxBrand}`}>
            <DatabaseRegular className={styles.statIcon} />
          </div>
          <div className={styles.storageContent}>
            <Text className={styles.storageValue}>
              {bytesToMB(stats.db_size_bytes)}
            </Text>
            <Text className={styles.storageLabel}>数据库大小</Text>
          </div>
        </Card>
        <Card className={styles.storageCard}>
          <div className={`${styles.iconBox} ${styles.iconBoxBrand}`}>
            <CloudRegular className={styles.statIcon} />
          </div>
          <div className={styles.storageContent}>
            <Text className={styles.storageValue}>
              {bytesToMB(stats.blob_size_bytes)}
            </Text>
            <Text className={styles.storageLabel}>Blob 大小</Text>
          </div>
        </Card>
      </div>

      {/* TLS 运行状态 */}
      <Text className={styles.sectionTitle}>TLS 运行状态</Text>
      <Card className={styles.storageCard} style={{ flexDirection: 'column', alignItems: 'stretch', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <LockClosedRegular />
          <Text weight="semibold">TLS 加密</Text>
          {tlsConfig?.active ? (
            <Badge appearance="filled" color="success">已加载</Badge>
          ) : (
            <Badge appearance="filled" color="subtle">未加载</Badge>
          )}
        </div>
        <div className={styles.statusGrid}>
          <div className={styles.statusRow}>
            <span style={{ color: tokens.colorNeutralForeground2 }}>最小 TLS 版本</span>
            <span>{tlsConfig?.min_version ?? '-'}</span>
          </div>
          <div className={styles.statusRow}>
            <span style={{ color: tokens.colorNeutralForeground2 }}>STARTTLS（25/587）</span>
            <span>{tlsConfig?.starttls_enabled ? '启用' : '禁用'}</span>
          </div>
          <div className={styles.statusRow}>
            <span style={{ color: tokens.colorNeutralForeground2 }}>IMAPS（993）</span>
            <span>{tlsConfig?.imaps_enabled ? '启用' : '禁用'}</span>
          </div>
          <div className={styles.statusRow}>
            <span style={{ color: tokens.colorNeutralForeground2 }}>POP3S（995）</span>
            <span>{tlsConfig?.pop3s_enabled ? '启用' : '禁用'}</span>
          </div>
        </div>
        <Divider />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            appearance="secondary"
            icon={reloadMutation.isPending ? <Spinner size="tiny" /> : <ArrowSyncRegular />}
            disabled={reloadMutation.isPending}
            onClick={() => reloadMutation.mutate()}
          >
            手动热重载 TLS
          </Button>
        </div>
      </Card>
    </div>
  )
}
